
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { recipeService } from '@/services/recipe';
import { mealCompositionService } from '@/services/mealComposition';
import { RECIPES, MEAL_COMPOSITIONS } from '@/constants/queryKeys';
import { UnifiedContent, ContentType } from '@/types/content';
import { RecipeWithStats } from '@/services/recipe/recipeStatsService';

export const useUnifiedContent = () => {
  const { data: recipes = [], isLoading: recipesLoading, refetch: refetchRecipes } = useQuery({
    queryKey: [RECIPES],
    queryFn: recipeService.getRecipes,
  });

  const { data: compositions = [], isLoading: compositionsLoading, refetch: refetchCompositions } = useQuery({
    queryKey: [MEAL_COMPOSITIONS],
    queryFn: mealCompositionService.getMealCompositions,
  });

  const unifiedContent = useMemo((): UnifiedContent[] => {
    const recipeContent: UnifiedContent[] = (recipes as RecipeWithStats[]).map(recipe => ({
      id: recipe.id,
      title: recipe.title,
      type: 'recipe' as ContentType,
      createdAt: recipe.createdAt,
      updatedAt: recipe.updatedAt,
      recipe,
    }));

    const compositionContent: UnifiedContent[] = compositions.map(composition => ({
      id: composition.id,
      title: composition.name,
      type: 'composition' as ContentType,
      createdAt: composition.createdAt,
      updatedAt: composition.updatedAt,
      composition,
    }));

    return [...recipeContent, ...compositionContent];
  }, [recipes, compositions]);

  const refetch = () => {
    refetchRecipes();
    refetchCompositions();
  };

  return {
    content: unifiedContent,
    recipes: recipes as RecipeWithStats[],
    compositions,
    isLoading: recipesLoading || compositionsLoading,
    refetch,
  };
};
